import { useContext } from 'react'
import { ShareVersionContext } from './ShareVersionContext'
import {
  getVersionedRoute,
  VersionedRoutes,
  RouteParam,
} from 'constants/routes'

interface GetVersionPathnameProps<Key extends keyof VersionedRoutes> {
  versionShortId: string | null
  isViewingLatestVersion: boolean
  routeKey: Key
  routeParams: RouteParam<Key>
}

export interface GetPathnameProps<Key extends keyof VersionedRoutes>
  extends Pick<GetVersionPathnameProps<Key>, 'routeKey' | 'routeParams'> {}

const getVersionedPathname = <Key extends keyof VersionedRoutes>({
  versionShortId,
  isViewingLatestVersion,
  routeKey,
  routeParams: params,
}: GetVersionPathnameProps<Key>) => {
  const route = getVersionedRoute(routeKey, isViewingLatestVersion)

  const routeParams =
    isViewingLatestVersion || !versionShortId
      ? params || {}
      : { ...(params || {}), versionShortId }

  return route.create(routeParams)
}

export const useVersioning = () => {
  const context = useContext(ShareVersionContext)!

  const {
    isViewingLatestVersion,
    versionShortId,
    share,
    latestVersion,
  } = context

  const getPathname = <Key extends keyof VersionedRoutes>(
    props: GetPathnameProps<Key>
  ) =>
    getVersionedPathname({
      ...props,
      isViewingLatestVersion,
      versionShortId,
    })

  return {
    ...context,
    getPathname,
    isLibrary: share?.libraryEnabled || false,
    latestVersion,
  }
}
